/**
 * A readable filename for a downloaded result JSON.
 *
 * Downloads used to land as `result_02d47888-766a-401e-b6fc-79fd7d3e5cb3_8c17017a-802d-4468-b01e-30815b970f48.json`
 * — unsortable, unsearchable, and impossible to tell apart in a Downloads folder
 * once you have a few optimization runs. This names them for what they are:
 *
 *   Auberge_Resorts_US_Display_2026-optimize-last-30-days-2026-08-18.json
 *   NRI_Perf_UAE_Pmax-optimize-2026-06-01_to_2026-06-30-2026-08-18.json
 *   Bernies_Playground_Q3-campaign-copy-2026-08-18.json
 *
 * The reporting window is included when the result carries one. Optimizers that
 * take no window (DV360, TTD, MadHive, YouTube DV360 — they analyse the whole
 * flight) simply have that segment left out rather than given a made-up default.
 */

/** Filesystem-safe, readable, and stable: keeps word characters, - and _. */
export function slugify(value, { maxLength = 60 } = {}) {
  const s = String(value ?? '').trim();
  if (!s) return '';
  return s
    .replace(/[^\w\s.-]/g, '')      // drop punctuation the OS or a shell would fight
    .replace(/[\s.]+/g, '_')        // spaces and dots → underscore
    .replace(/_{2,}/g, '_')
    .replace(/^[_-]+|[_-]+$/g, '')
    .slice(0, maxLength)
    .replace(/[_-]+$/g, '');        // don't end on a separator after the slice
}

/** YYYY-MM-DD in the viewer's timezone, from anything Date can parse. */
export function dateStamp(value) {
  const d = value ? new Date(value) : new Date();
  if (Number.isNaN(d.getTime())) return dateStamp(null);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * The reporting window a result was produced over, as a filename segment, or ''.
 *
 * Read from the places the optimizers actually echo it — an explicit range wins
 * over a preset, because it says more.
 */
export function windowSegment(jsonResponse) {
  const r = jsonResponse && typeof jsonResponse === 'object' ? jsonResponse : {};
  const d = r.details && typeof r.details === 'object' ? r.details : {};
  const start = d.start_date || r.start_date;
  const end = d.end_date || r.end_date;
  if (start && end) return `${slugify(start, { maxLength: 12 })}_to_${slugify(end, { maxLength: 12 })}`;

  const range = d.date_range || r.date_range;
  if (typeof range === 'string' && range.trim()) return slugify(range.replace(/\s*(–|—|to)\s*/gi, '_to_'), { maxLength: 30 });
  if (range && typeof range === 'object' && range.start_date && range.end_date) {
    return `${slugify(range.start_date, { maxLength: 12 })}_to_${slugify(range.end_date, { maxLength: 12 })}`;
  }

  const preset = d.date_preset || r.date_preset;
  if (preset) return slugify(String(preset).replace(/_/g, '-'), { maxLength: 20 });

  const daysBack = d.days_back ?? r.days_back;
  if (Number.isFinite(Number(daysBack)) && Number(daysBack) > 0) return `last-${Number(daysBack)}-days`;

  return '';
}

/**
 * @param {object} args
 * @param {string} [args.campaignName]  the campaign this result belongs to
 * @param {string} [args.action]        message action, e.g. 'optimize-campaign'
 * @param {object} [args.jsonResponse]  the result being downloaded
 * @param {string|number|Date} [args.timestamp] when the run happened
 * @param {string} [args.fallbackId]    used only when there is no campaign name
 * @returns {string} filename including the .json extension
 */
export function buildResultDownloadName({
  campaignName, action, jsonResponse, timestamp, fallbackId,
} = {}) {
  const parts = [
    slugify(campaignName) || `result_${slugify(fallbackId, { maxLength: 20 }) || 'download'}`,
    // 'optimize-campaign' reads better than 'optimizeCampaign' or 'optimize_campaign'.
    slugify(String(action || '').replace(/_/g, '-'), { maxLength: 30 }).replace(/_/g, '-'),
    windowSegment(jsonResponse),
    dateStamp(timestamp),
  ].filter(Boolean);
  return `${parts.join('-')}.json`;
}

export default { buildResultDownloadName, windowSegment, dateStamp, slugify };
